import React from 'react';
import {Box, Card, CardActionArea, CardContent, Grid2, Typography} from '@mui/material';
import {ChevronRight, Flight} from '@mui/icons-material';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import {t} from 'ttag';
import useFadeNavigate from './useFadeNavigate';

// Not yet available in production — flip to true once the Flight Declaration flow is ready to launch.
const FLIGHT_DECLARATION_ENABLED = true;

const DeclarationsPage = () => {
  const navigate = useFadeNavigate();

  return (
    <Box>
      <Box sx={{textAlign: 'center', mb: 4}}>
        <Box sx={{display: 'flex', justifyContent: 'center', mb: 2}}>
          <Box sx={{
            bgcolor: 'primary.main', borderRadius: 2.5,
            width: 56, height: 56,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 4px 12px rgba(63,81,181,0.3)'
          }}>
            <ArticleOutlinedIcon sx={{color: 'white', fontSize: '1.75rem'}} />
          </Box>
        </Box>
        <Typography variant='h5' fontWeight='bold' gutterBottom>
          {t`Declaration Issuance`}
        </Typography>
        <Typography variant='body2' color='text.secondary'>
          {t`Select the type of declaration to issue.`}
        </Typography>
      </Box>

      <Grid2 container spacing={3} justifyContent='center'>
        {FLIGHT_DECLARATION_ENABLED && (
          <Grid2 size={{xs: 12, sm: 6, md: 5}}>
            <Card
              variant='outlined'
              sx={{
                height: '100%',
                borderTop: '3px solid #3f51b5',
                transition: 'box-shadow 0.2s ease, transform 0.15s ease',
                '&:hover': {boxShadow: 4, transform: 'translateY(-2px)'},
              }}
            >
              <CardActionArea sx={{height: '100%'}} onClick={() => navigate('/declarations/flight')}>
                <CardContent sx={{p: 3}}>
                  <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2}}>
                    <Box sx={{
                      bgcolor: 'rgba(63, 81, 181, 0.1)', borderRadius: 2,
                      width: 52, height: 52,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <Flight sx={{color: '#3f51b5', fontSize: '1.5rem'}} />
                    </Box>
                    <ChevronRight sx={{color: 'text.disabled', mt: 0.5}} />
                  </Box>
                  <Typography fontWeight='bold' variant='body1' gutterBottom>
                    {t`Flight Declaration`}
                  </Typography>
                  <Typography variant='body2' color='text.secondary' sx={{mb: 2}}>
                    {t`Issue delay, cancellation or boarding declarations associated with a specific flight.`}
                  </Typography>
                  <Box sx={{display: 'flex', gap: 0.75, flexWrap: 'wrap'}}>
                    {([
                      {label: t`Delay`, color: '#bf360c', bg: 'rgba(230,81,0,0.12)'},
                      {label: t`Cancellation`, color: '#b71c1c', bg: 'rgba(198,40,40,0.12)'},
                      {label: t`Boarding`, color: '#0d47a1', bg: 'rgba(21,101,192,0.12)'},
                    ] as const).map(({label, color, bg}) => (
                      <Box key={label} sx={{display: 'inline-flex', alignItems: 'center', gap: 0.6, px: 1.25, py: 0.6, borderRadius: '20px', bgcolor: bg}}>
                        <Box sx={{width: 6, height: 6, borderRadius: '50%', bgcolor: color, flexShrink: 0, alignSelf: 'center'}} />
                        <Box component='span' sx={{fontSize: '0.75rem', fontWeight: 500, color, letterSpacing: '0.02em', lineHeight: 1.4}}>
                          {label}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid2>
        )}

        <Grid2 size={FLIGHT_DECLARATION_ENABLED ? {xs: 12, sm: 6, md: 5} : {xs: 12, sm: 8, md: 6}}>
          <Card
            variant='outlined'
            sx={{
              height: '100%',
              borderTop: '3px solid #ff9800',
              transition: 'box-shadow 0.2s ease, transform 0.15s ease',
              '&:hover': {boxShadow: 4, transform: 'translateY(-2px)'},
            }}
          >
            <CardActionArea sx={{height: '100%'}} onClick={() => navigate('/declarations/ticket-usage')}>
              <CardContent sx={{p: 3}}>
                <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2}}>
                  <Box sx={{
                    bgcolor: 'rgba(255, 152, 0, 0.15)', borderRadius: 2,
                    width: 52, height: 52,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <ConfirmationNumberIcon sx={{color: '#ff9800', fontSize: '1.5rem'}} />
                  </Box>
                  <ChevronRight sx={{color: 'text.disabled', mt: 0.5}} />
                </Box>
                <Typography fontWeight='bold' variant='body1' gutterBottom>
                  {t`Ticket Usage Declaration`}
                </Typography>
                <Typography variant='body2' color='text.secondary' sx={{mb: 2}}>
                  {t`Issue a usage declaration (TKT usage) based on the electronic ticket number.`}
                </Typography>
                <Box sx={{display: 'flex', gap: 0.75, flexWrap: 'wrap'}}>
                  <Box sx={{display: 'inline-flex', alignItems: 'center', gap: 0.6, px: 1.25, py: 0.6, borderRadius: '20px', bgcolor: 'rgba(230,81,0,0.12)'}}>
                    <Box sx={{width: 6, height: 6, borderRadius: '50%', bgcolor: '#bf360c', flexShrink: 0, alignSelf: 'center'}} />
                    <Box component='span' sx={{fontSize: '0.75rem', fontWeight: 500, color: '#bf360c', letterSpacing: '0.02em', lineHeight: 1.4}}>
                      TKT Usage
                    </Box>
                  </Box>
                </Box>
              </CardContent>
            </CardActionArea>
          </Card>
        </Grid2>
      </Grid2>
    </Box>
  );
};

export default DeclarationsPage;
